import { useTranslation } from 'react-i18next';
import { formatGetal } from '../format';
import { HOOGTE_KLASSE, procentStap, trendKleur, type TrendKleur, type TrendPlek } from '../leerkaart';
import { weeknummer } from '../week';

export interface TrendStavenProps {
  /** Vier vaste plekken, oudste eerst, uit `trendPlekken`. */
  plekken: TrendPlek[];
}

/** Kleur, woord en teken per vergelijking (DESIGN.md Trendblokje, A7). Kleur is nooit het enige signaal. */
const VERGELIJKING: Record<TrendKleur, { staaf: string; tekst: string; woord: string; teken: string | null }> = {
  groen: { staaf: 'bg-[var(--success-text)]', tekst: 'text-[var(--success-text)]', woord: 'qa.eigen.trendMinderVaak', teken: '▼' },
  oranje: { staaf: 'bg-[var(--warning-text)]', tekst: 'text-[var(--warning-text)]', woord: 'qa.eigen.trendVaker', teken: '▲' },
  grijs: { staaf: 'bg-[var(--foreground-muted)]', tekst: 'text-[var(--foreground-muted)]', woord: 'qa.eigen.trendGelijk', teken: null },
  gestreept: { staaf: 'werkkwaliteit-streep', tekst: 'text-[var(--foreground-muted)]', woord: 'qa.eigen.trendGeenVergelijking', teken: null },
};

const KLEIN = 'w-full text-center text-[0.6875rem] text-[var(--foreground-muted)] hyphens-auto [overflow-wrap:anywhere]';
const PLEK = 'flex min-w-0 flex-1 flex-col items-center gap-1';

/**
 * Het blokje "komt dit terug": vier vaste plekken, oudste links (ux-spec 3.4, `trend-en-maand.md`, A7).
 *
 * Een staaf draagt altijd "X van Y" en het woord van de vergelijking met de plek ervoor ("minder vaak",
 * "vaker", "gelijk", "geen vergelijking"), met ▼ of ▲ erboven (`aria-hidden`). De hoogte is de verhouding en
 * staat nergens als getal. Een werksoort of veld onder de grens is gestreept op een vaste lage hoogte zonder
 * getal; een week zonder gegevens is een lege plek. Elke plek is een eigen beeld met zijn eigen `aria-label`;
 * er is geen omhullend beeld. De legenda staat eronder, als gewone tekst.
 */
export function TrendStaven({ plekken }: TrendStavenProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const xVanY = (plek: Extract<TrendPlek, { soort: 'staaf' }>) => ({
    aantal: formatGetal(plek.aantal, locale),
    totaal: formatGetal(plek.totaal, locale),
  });

  return (
    <div className="flex w-full max-w-[340px] flex-col gap-2">
      <div className="flex items-end justify-between gap-2">
        {plekken.map((plek, index) => {
          const nummer = weeknummer(plek.weekKey);
          const weeklabel = <span className={KLEIN}>{t('qa.eigen.trendWeekLabel', { lng: 'nl', nummer })}</span>;

          // Zichtbaar en in het aria-label, nooit alleen bij aanwijzen: de module kent geen `title` (UX-DR20).
          if (plek.soort === 'geenGegevens') {
            return (
              <div
                aria-label={t('qa.eigen.trendWeekAriaGeenGegevens', { nummer })}
                className={PLEK}
                data-testid={`werkkwaliteit-trendplek-${plek.weekKey}`}
                key={plek.weekKey}
                role="img"
              >
                <span className={KLEIN}>{t('qa.eigen.trendGeenGegevens')}</span>
                {weeklabel}
              </div>
            );
          }

          if (plek.soort !== 'staaf') {
            const teWeinigProducten = plek.soort === 'teWeinigProducten';
            return (
              <div
                aria-label={t(teWeinigProducten ? 'qa.eigen.trendWeekAriaTeWeinig' : 'qa.eigen.trendWeekAriaTeWeinigGegevens', { nummer })}
                className={PLEK}
                data-kleur="gestreept"
                data-testid={`werkkwaliteit-trendplek-${plek.weekKey}`}
                key={plek.weekKey}
                role="img"
              >
                <span className={KLEIN}>{t(teWeinigProducten ? 'qa.eigen.trendTeWeinig' : 'qa.eigen.trendTeWeinigGegevens')}</span>
                {/* Vaste lage hoogte (vijf procent): de verhouding telt hier niet. */}
                <div className={`w-full rounded-[4px] werkkwaliteit-streep ${HOOGTE_KLASSE[5]}`} />
                {weeklabel}
              </div>
            );
          }

          const kleur = trendKleur(index === 0 ? null : (plekken[index - 1] ?? null), plek);
          const { staaf, tekst, woord, teken } = VERGELIJKING[kleur];
          const hoogte = HOOGTE_KLASSE[procentStap((plek.aantal / plek.totaal) * 100)] ?? '';
          return (
            <div
              aria-label={t('qa.eigen.trendWeekAriaWoord', { nummer, ...xVanY(plek), woord: t(woord) })}
              className={PLEK}
              data-kleur={kleur}
              data-testid={`werkkwaliteit-trendplek-${plek.weekKey}`}
              key={plek.weekKey}
              role="img"
            >
              <span className="text-[0.6875rem] font-semibold text-[var(--foreground)]">{t('qa.eigen.trendStaaf', xVanY(plek))}</span>
              {teken !== null && (
                <span aria-hidden="true" className={`text-[0.6875rem] leading-none ${tekst}`}>
                  {teken}
                </span>
              )}
              <div className={`w-full rounded-[4px] ${staaf} ${hoogte}`} />
              <span className={`w-full text-center text-[0.6875rem] font-medium leading-tight hyphens-auto [overflow-wrap:anywhere] ${tekst}`}>{t(woord)}</span>
              {weeklabel}
            </div>
          );
        })}
      </div>
      <p className="text-[0.75rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-trend-legenda">
        {t('qa.eigen.trendLegenda')}
      </p>
    </div>
  );
}
